import { arrayUnion, collection, doc, getDocs, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { UserPlus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { User } from '@/slw/entities/chat';
import { useDebounce } from '@/slw/shared/hooks/use-debounce';
import { db } from '@/slw/shared/lib/firebase/config';
import { useAuthStore } from '@/slw/shared/stores/auth.store';
import { SelectOption } from '@/slw/shared/types/common.type';
import { AutoComplete } from '@/slw/shared/ui/autocomplete';
import { Button } from '@/slw/shared/ui/button';
import { Dialog, DialogContent } from '@/slw/shared/ui/dialog';

export const AddUserChat = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const [selectedUser, setSelectedUser] = useState<SelectOption | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const { user: currentUser } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);

  const [users, setUsers] = useState<User[]>([]);

  useEffect(() => {
    const searchUser = async () => {
      try {
        setIsLoading(true);
        const userRef = collection(db, 'users');

        // Sử dụng toán tử '>=' và '<=' để tìm kiếm theo tiền tố (prefix search)
        // Điều này sẽ tìm tất cả các username bắt đầu bằng debouncedSearchTerm
        const q = query(userRef, where('username', '==', debouncedSearchTerm));

        const querySnapShot = await getDocs(q);

        if (!querySnapShot.empty) {
          setUsers(querySnapShot.docs.map((doc) => doc.data() as User));
        }
      } catch (err) {
        // Error handled
      } finally {
        setIsLoading(false);
      }
    };

    searchUser();
  }, [debouncedSearchTerm]);

  // Transform API response to SelectOption format
  const userOptions = useMemo(() => {
    if (!users) return [];

    return users
      .filter((user: User) => user.id !== currentUser?.id)
      .map((user: User) => ({
        value: user.id!,
        label: `${user.username}(${user.email})`,
      }));
  }, [users, currentUser?.id]);

  const handleSearchAsync = async (searchValue: string) => {
    setSearchTerm(searchValue);

    if (!searchValue) {
      setSelectedUser(undefined);

      return;
    }
  };

  const handleAdd = async () => {
    if (!selectedUser) return;

    setIsAdding(true);
    const chatRef = collection(db, 'chats');
    const userChatsRef = collection(db, 'userchats');
    const userData = selectedUser as SelectOption;

    try {
      const newChatRef = doc(chatRef);

      await setDoc(newChatRef, {
        createdAt: serverTimestamp(),
        messages: [],
        messageCount: 0,
      });
      const chatData = {
        chatId: newChatRef.id,
        lastMessage: '',
        updatedAt: new Date(),
        isSeen: false,
      };

      await setDoc(
        doc(userChatsRef, userData?.value || ''),
        {
          chats: arrayUnion({
            ...chatData,
            receiverId: currentUser?.id || '',
          }),
        },
        { merge: true },
      );

      await setDoc(
        doc(userChatsRef, currentUser?.id || ''),
        {
          chats: arrayUnion({
            ...chatData,
            receiverId: userData.value || '',
          }),
        },
        { merge: true },
      );

      // Reset after successful add
      setSelectedUser(undefined);
      setSearchTerm('');
      onClose(); // Close modal after successful add
    } catch (err) {
      // Error handled
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
        className='md:w-[40rem] w-[20rem] gap-4 max-h-screen md:max-h-[900px]'
      >
        <h2 className='text-xl font-semibold text-gray-800 mb-2'>Add User</h2>
        <div className='w-full flex flex-col gap-4'>
          <div className='flex flex-col gap-2'>
            <AutoComplete
              options={userOptions}
              value={selectedUser}
              onValueChange={setSelectedUser}
              onSearchAsync={handleSearchAsync}
              isLoading={isLoading}
              shouldFilterAsync={true}
              placeholder='Type username to search...'
              emptyMessage='No users found'
              className='w-full'
            />
          </div>
          <div className='flex justify-end gap-2'>
            <Button onClick={onClose} disabled={isAdding} color='secondary' variant='outline'>
              Cancel
            </Button>

            <Button onClick={handleAdd} disabled={!selectedUser || isAdding}>
              {isAdding ? (
                <>
                  <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin' />
                  Adding...
                </>
              ) : (
                <>
                  <UserPlus className='w-4 h-4' />
                  Add User
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
